import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../api/client";
import "../styles/rules.css";

const contractTypes = ["T&M", "Fixed Bid", "Custom", "Time and Material", "Managed Services"];

// Helper to get user email from localStorage
function getUserEmail() {
  const direct = (
    localStorage.getItem("user_email") ||
    localStorage.getItem("email") ||
    ""
  ).trim();

  if (direct) return direct.toLowerCase();

  try {
    const u = JSON.parse(
      localStorage.getItem("user") ||
      localStorage.getItem("currentUser") ||
      "null"
    );
    if (u?.email) return String(u.email).trim().toLowerCase();
  } catch {}

  return "";
}

function safeParseRaw(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function clampPct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function getScoreColor(score) {
  if (score === null || score === undefined) return "#9ca3af";
  if (score >= 80) return "#10b981";
  if (score >= 70) return "#3b82f6";
  if (score >= 50) return "#f59e0b";
  if (score >= 30) return "#f97316";
  return "#ef4444";
}

function getScoreLabel(score) {
  if (score === null || score === undefined) return "N/A";
  if (score >= 80) return "Excellent";
  if (score >= 70) return "Good";
  if (score >= 50) return "Fair";
  if (score >= 30) return "Poor";
  return "Critical";
}

export default function SowAnalysisViewerPage() {
  // --- Tab state ---
  const [activeTab, setActiveTab] = useState("all"); // "all", "sfdc", "ui"
  
  // --- Opportunities state ---
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(null);
  const [opportunitySearch, setOpportunitySearch] = useState("");
  
  // --- Analysis history state ---
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // --- UI state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- User email for visibility filtering ---
  const userEmail = useMemo(() => getUserEmail(), []);

  // ----------------------------------------------------
  // Load Opportunities (with user email for visibility)
  // ----------------------------------------------------
  const loadOpportunities = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      
      // Build URL with user_email param for visibility filtering
      const params = new URLSearchParams();
      if (userEmail) {
        params.append("user_email", userEmail);
      }
      
      const url = `/opportunities${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url);
      const list = resp.data || [];
      setOpportunities(list);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        setError("Authentication required. Please log in.");
      } else {
        setError("Failed to load opportunities.");
      }
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  // ----------------------------------------------------
  // Load Analysis History (with source filter and user email for SFDC visibility)
  // ----------------------------------------------------
  const loadAnalysisHistory = useCallback(async (source = null, opportunityId = null) => {
    try {
      setError("");
      const params = new URLSearchParams();
      
      // Map tab to source
      if (source && source !== "all") {
        params.append("source", source === "sfdc" ? "SFDC" : "UI_TEST");
      }
      
      if (opportunityId) {
        params.append("opportunity_id", opportunityId);
      }
      
      // Always send user email for SFDC visibility filtering
      if (userEmail) {
        params.append("user_email", userEmail);
      }
      
      const url = `/sow-tests/history${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url);
      const list = resp.data || [];
      setAnalysisHistory(list);
      
      // Auto-select first if available
      if (list.length > 0 && !selectedAnalysisId && list[0]?.id != null) {
        await selectAnalysis(list[0].id, list[0]);
      }
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        setError("Authentication required for SFDC records. Please ensure you're logged in.");
      } else {
        setError("Failed to load analysis history.");
      }
    }
  }, [selectedAnalysisId, userEmail]);

  // ----------------------------------------------------
  // Initial load
  // ----------------------------------------------------
  useEffect(() => {
    loadOpportunities();
    loadAnalysisHistory(activeTab);
  }, []);

  // ----------------------------------------------------
  // Reload when tab changes
  // ----------------------------------------------------
  useEffect(() => {
    setSelectedAnalysisId(null);
    setSelectedDetail(null);
    loadAnalysisHistory(activeTab, selectedOpportunityId);
  }, [activeTab, selectedOpportunityId]);

  // ----------------------------------------------------
  // Filter opportunities
  // ----------------------------------------------------
  const filteredOpportunities = useMemo(() => {
    const q = (opportunitySearch || "").trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter((o) => {
      const hay = [
        o.id,
        o.sf_opportunity_id,
        o.opportunity_name,
        o.account_name,
        o.contract_type,
        o.stage,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [opportunities, opportunitySearch]);

  // ----------------------------------------------------
  // Select an analysis -> fetch detail (with user email for SFDC)
  // ----------------------------------------------------
  const selectAnalysis = async (id, baseRow, opts = {}) => {
    setSelectedAnalysisId(id);
    setDetailLoading(true);
    setSelectedDetail(baseRow || null);

    try {
      // Include user_email param for SFDC visibility check
      const params = new URLSearchParams();
      if (userEmail) {
        params.append("user_email", userEmail);
      }
      
      const url = `/sow-tests/history/${id}${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url);
      setSelectedDetail(resp.data);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setError("You don't have access to this SFDC record.");
      } else if (err?.response?.status === 401) {
        setError("Authentication required for SFDC records.");
      } else if (!opts?.silent) {
        setError("Failed to load analysis detail.");
      }
      setSelectedDetail(baseRow || null);
    } finally {
      setDetailLoading(false);
    }
  };

  // ----------------------------------------------------
  // Select an opportunity
  // ----------------------------------------------------
  const selectOpportunity = (id) => {
    setSelectedOpportunityId(id === selectedOpportunityId ? null : id);
    setSelectedAnalysisId(null);
    setSelectedDetail(null);
  };

  // ----------------------------------------------------
  // Downloads (with user email for SFDC visibility)
  // ----------------------------------------------------
  const downloadExcel = async (id) => {
    if (!id) return;
    try {
      // Include user_email param for SFDC visibility check
      const params = new URLSearchParams();
      if (userEmail) {
        params.append("user_email", userEmail);
      }
      
      const url = `/sow-tests/report-excel/${id}${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url, {
        responseType: "blob",
        timeout: 300000,
      });
      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const dlUrl = window.URL.createObjectURL(blob);
      link.href = dlUrl;
      link.download = `sow_analysis_${id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(dlUrl);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setError("You don't have access to download this SFDC record.");
      } else {
        setError("Failed to download Excel report.");
      }
    }
  };

  const downloadSowFile = async (id) => {
    if (!id) return;
    try {
      // Include user_email param for SFDC visibility check
      const params = new URLSearchParams();
      if (userEmail) {
        params.append("user_email", userEmail);
      }
      
      const url = `/sow-tests/files/${id}${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url, {
        responseType: "blob",
        timeout: 300000,
      });
      const cd = resp.headers["content-disposition"] || "";
      let filename = "sow_document";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      if (match?.[1]) filename = match[1];
      const blob = new Blob([resp.data]);
      const link = document.createElement("a");
      const dlUrl = window.URL.createObjectURL(blob);
      link.href = dlUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(dlUrl);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setError("You don't have access to download this SFDC record.");
      } else {
        setError("Failed to download SOW file.");
      }
    }
  };

  const downloadJson = async (id) => {
    if (!id || !selectedDetail) return;
    try {
      const raw = safeParseRaw(selectedDetail.raw_json);
      const jsonStr = JSON.stringify(raw, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const link = document.createElement("a");
      const dlUrl = window.URL.createObjectURL(blob);
      link.href = dlUrl;
      link.download = `sow_analysis_${id}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(dlUrl);
    } catch (err) {
      console.error(err);
      setError("Failed to download JSON.");
    }
  };

  // ----------------------------------------------------
  // Render Score Card
  // ----------------------------------------------------
  const renderScoreCard = (label, score, rationale) => {
    const color = getScoreColor(score);
    const scoreLabel = getScoreLabel(score);
    
    return (
      <div style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
        minWidth: 180,
        flex: 1,
      }}>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 700, color }}>{score ?? "-"}</span>
          <span style={{ fontSize: 12, color }}>{scoreLabel}</span>
        </div>
        {rationale && (
          <div style={{ 
            fontSize: 11, 
            color: "#6b7280", 
            marginTop: 8,
            maxHeight: 60,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {rationale.substring(0, 150)}{rationale.length > 150 ? "..." : ""}
          </div>
        )}
      </div>
    );
  };

  // ----------------------------------------------------
  // Render Rule Details Tables
  // ----------------------------------------------------
  const renderRuleDetails = (raw) => {
    const ruleEvals = raw.rule_evaluations_enriched || raw.rule_evaluations || [];
    if (!ruleEvals.length) {
      return (
        <p style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>
          No rule-by-rule details available.
        </p>
      );
    }

    const missingRows = ruleEvals.filter(
      (r) => (r.status || "").toLowerCase() === "missing"
    );
    const missingMandatory = raw.missing_mandatory_rules || [];

    return (
      <div style={{ marginTop: 18 }}>
        {/* Missing Mandatory Warning */}
        {missingMandatory.length > 0 && (
          <div style={{
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}>
            <div style={{ fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
              ⚠ {missingMandatory.length} Mandatory Rules Missing
            </div>
            <div style={{ fontSize: 12, color: "#78350f" }}>
              {missingMandatory.join(", ")}
            </div>
          </div>
        )}

        {/* Missing Sections */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Missing Sections ({missingRows.length})
          </h3>
          <div className="rules-table-wrapper">
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Rule Name</th>
                  <th>Category</th>
                  <th>Dimension</th>
                  <th>Severity</th>
                  <th>Mandatory</th>
                  <th>Weight</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {missingRows.map((r, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 500 }}>{r.rule_name || r.name}</td>
                    <td>{r.engine_category || "-"}</td>
                    <td>{r.engine_dimension || "-"}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      <span style={{
                        padding: "2px 6px",
                        borderRadius: 4,
                        fontSize: 11,
                        background: r.engine_severity === "critical" ? "#fee2e2" :
                          r.engine_severity === "high" ? "#fef3c7" :
                          r.engine_severity === "medium" ? "#e0f2fe" : "#f3f4f6",
                        color: r.engine_severity === "critical" ? "#991b1b" :
                          r.engine_severity === "high" ? "#92400e" :
                          r.engine_severity === "medium" ? "#0369a1" : "#374151",
                      }}>
                        {r.engine_severity || r.severity || "-"}
                      </span>
                    </td>
                    <td>{r.engine_mandatory ? "Yes" : "No"}</td>
                    <td>{r.engine_weight_used?.toFixed(4) || "-"}</td>
                    <td style={{ whiteSpace: "normal", maxWidth: 200 }}>
                      {r.suggestion || r.comments || "-"}
                    </td>
                  </tr>
                ))}
                {missingRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      ✓ No missing rules - all rules addressed
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Full Rule Evaluations */}
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          All Rule Evaluations ({ruleEvals.length})
        </h3>
        <div className="rules-table-wrapper">
          <table className="rules-table">
            <thead>
              <tr>
                <th>Rule Name</th>
                <th>Category</th>
                <th>Dimension</th>
                <th>Severity</th>
                <th>Mandatory</th>
                <th>Status</th>
                <th>Score</th>
                <th>Weight</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              {ruleEvals.map((r, idx) => {
                const status = (r.status || "").toLowerCase();
                return (
                  <tr key={idx} style={{
                    background: status === "covered" ? "#f0fdf4" :
                      status === "partially_covered" ? "#fefce8" :
                      status === "missing" ? "#fef2f2" : "transparent"
                  }}>
                    <td style={{ fontWeight: 500 }}>{r.rule_name || r.name}</td>
                    <td>{r.engine_category || "-"}</td>
                    <td>{r.engine_dimension || "-"}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {r.engine_severity || r.severity || "-"}
                    </td>
                    <td>{r.engine_mandatory ? "Yes" : "No"}</td>
                    <td>
                      <span style={{
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        background: status === "covered" ? "#dcfce7" :
                          status === "partially_covered" ? "#fef9c3" : "#fee2e2",
                        color: status === "covered" ? "#166534" :
                          status === "partially_covered" ? "#854d0e" : "#991b1b",
                      }}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{r.rule_score ?? "-"}</td>
                    <td>{r.engine_weight_used?.toFixed(4) || "-"}</td>
                    <td style={{ whiteSpace: "normal", maxWidth: 200, fontSize: 11 }}>
                      {r.comments || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------
  // Render Calculation Details
  // ----------------------------------------------------
  const renderCalculationDetails = (raw) => {
    const totalWeight = raw.total_weight_used || raw.total_weight || 0;
    const weightedSum = raw.weighted_sum || 0;
    const rulesCovered = raw.rules_covered || 0;
    const rulesPartial = raw.rules_partial || 0;
    const rulesMissing = raw.rules_missing || 0;
    const totalRules = rulesCovered + rulesPartial + rulesMissing;

    return (
      <div style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
      }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
          Score Calculation Details
        </h4>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Formula</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>
              (Weighted Sum ÷ Total Weight) × 100
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Weighted Sum</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{weightedSum.toFixed(4)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Total Weight</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{totalWeight.toFixed(4)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Total Rules</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{totalRules}</div>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: "#10b981" }} />
            <span style={{ fontSize: 11 }}>Covered: {rulesCovered}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: "#f59e0b" }} />
            <span style={{ fontSize: 11 }}>Partial: {rulesPartial}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: "#ef4444" }} />
            <span style={{ fontSize: 11 }}>Missing: {rulesMissing}</span>
          </div>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------
  // Render Detail Card
  // ----------------------------------------------------
  const renderDetailCard = () => {
    if (!selectedAnalysisId) {
      return (
        <div className="rules-form">
          <h2>Analysis Detail</h2>
          <p style={{ fontSize: 13, color: "#6b7280" }}>
            Select an analysis from the list to view details.
          </p>
        </div>
      );
    }

    const sel = selectedDetail || analysisHistory.find((h) => h.id === selectedAnalysisId);
    if (!sel) {
      return (
        <div className="rules-form">
          <h2>Analysis Detail</h2>
          <p style={{ fontSize: 13, color: "#6b7280" }}>Record not found.</p>
        </div>
      );
    }

    const raw = safeParseRaw(sel.raw_json);
    const finalOverall = raw.final_overall_score_used ?? raw.computed_overall_score ?? sel.score_overall ?? 0;
    const riskScore = raw.risk_score ?? sel.score_risk;
    const complianceScore = raw.compliance_score ?? sel.score_compliance;
    const commercialScore = raw.commercial_score ?? sel.score_commercial;

    const issues = raw.issues || [];
    const recs = raw.recommendations || [];
    const hasFile = Boolean(sel.file_path);

    return (
      <div className="rules-form" style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              Analysis #{sel.id}
              {sel.source && (
                <span style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: sel.source === "SFDC" ? "#dbeafe" : "#f3e8ff",
                  color: sel.source === "SFDC" ? "#1e40af" : "#7c3aed",
                }}>
                  {sel.source}
                </span>
              )}
              {detailLoading && (
                <span style={{ fontSize: 12, color: "#6b7280" }}>Loading…</span>
              )}
            </h2>
            <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>
              <strong>Opportunity:</strong> {sel.opportunity_name || "-"}
            </p>
            <p style={{ fontSize: 13, color: "#374151", margin: "4px 0" }}>
              <strong>Account:</strong> {sel.account_name || "-"}
            </p>
            <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>
              <strong>Contract Type:</strong> {sel.contract_type || "-"} | <strong>Stage:</strong> {sel.stage || "-"}
            </p>
            {sel.sf_opportunity_id && (
              <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0" }}>
                SF ID: {sel.sf_opportunity_id}
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="secondary" onClick={() => downloadExcel(sel.id)}>
              📊 Excel
            </button>
            <button type="button" className="secondary" onClick={() => downloadJson(sel.id)}>
              📄 JSON
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => downloadSowFile(sel.id)}
              disabled={!hasFile}
              title={!hasFile ? "No file stored" : "Download SOW"}
            >
              📎 SOW
            </button>
          </div>
        </div>

        {/* Score Cards */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          {renderScoreCard("Overall Score", finalOverall, raw.overall_rationale)}
          {renderScoreCard("Risk Score", riskScore, raw.risk_rationale)}
          {renderScoreCard("Compliance Score", complianceScore, raw.compliance_rationale)}
          {renderScoreCard("Commercial Score", commercialScore, raw.commercial_rationale)}
        </div>

        {/* Summary */}
        <div style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Summary</div>
          <div style={{ fontSize: 12, color: "#0c4a6e" }}>
            {raw.summary || sel.summary || "No summary available."}
          </div>
        </div>

        {/* Calculation Details */}
        {renderCalculationDetails(raw)}

        {/* Issues */}
        {issues.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              Key Issues ({issues.length})
            </h3>
            <div className="rules-table-wrapper">
              <table className="rules-table">
                <thead>
                  <tr>
                    <th>Dimension</th>
                    <th>Severity</th>
                    <th>Description</th>
                    <th>Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((iss, idx) => (
                    <tr key={idx}>
                      <td>{iss.dimension || "-"}</td>
                      <td style={{ textTransform: "capitalize" }}>{iss.severity || "-"}</td>
                      <td style={{ whiteSpace: "normal" }}>{iss.description || "-"}</td>
                      <td>{iss.rule_name || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              Recommendations ({recs.length})
            </h3>
            <ul style={{ paddingLeft: 18, fontSize: 12, margin: 0 }}>
              {recs.map((r, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Rule Details */}
        {renderRuleDetails(raw)}
      </div>
    );
  };

  // ----------------------------------------------------
  // Render Opportunities List
  // ----------------------------------------------------
  const renderOpportunitiesList = () => {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
            Filter by Opportunity
          </h3>
          {selectedOpportunityId && (
            <button
              type="button"
              className="secondary"
              style={{ padding: "4px 8px", fontSize: 11 }}
              onClick={() => selectOpportunity(null)}
            >
              Clear Filter
            </button>
          )}
        </div>
        
        <input
          value={opportunitySearch}
          onChange={(e) => setOpportunitySearch(e.target.value)}
          placeholder="Search opportunities..."
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            fontSize: 12,
            marginBottom: 8,
          }}
        />

        <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {filteredOpportunities.map((opp) => (
            <div
              key={opp.id}
              onClick={() => selectOpportunity(opp.id)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #f3f4f6",
                background: selectedOpportunityId === opp.id ? "#eff6ff" : "transparent",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500 }}>{opp.opportunity_name}</div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {opp.account_name} | {opp.contract_type || "-"} | {opp.stage || "-"}
              </div>
              {opp.sf_opportunity_id && (
                <div style={{ fontSize: 10, color: "#9ca3af" }}>
                  SF: {opp.sf_opportunity_id}
                </div>
              )}
            </div>
          ))}
          {filteredOpportunities.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: "#6b7280", textAlign: "center" }}>
              No opportunities found
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rules-page">
      <header className="rules-header">
        <div>
          <h1>SOW Analysis Viewer</h1>
          <p>
            View and explore SOW analysis results from SFDC and UI uploads.
            {userEmail && (
              <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>
                (Viewing as: {userEmail})
              </span>
            )}
          </p>
        </div>
      </header>

      <div className="rules-layout analysis-layout">
        {/* LEFT PANEL */}
        <section className="rules-list">
          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[
              { key: "all", label: "All" },
              { key: "sfdc", label: "SFDC" },
              { key: "ui", label: "UI Upload" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: activeTab === tab.key ? "2px solid #2563eb" : "1px solid #d1d5db",
                  background: activeTab === tab.key ? "#eff6ff" : "white",
                  color: activeTab === tab.key ? "#1d4ed8" : "#374151",
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {error && <div className="rules-error">{error}</div>}

          {/* Opportunities Filter */}
          {renderOpportunitiesList()}

          <hr style={{ margin: "16px 0", borderColor: "#e5e7eb" }} />

          {/* Analysis History */}
          <div className="rules-list-header">
            <h2 style={{ margin: 0 }}>Analysis History</h2>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {analysisHistory.length} records
            </span>
          </div>

          <div className="rules-table-wrapper" style={{ marginTop: 12 }}>
            <table className="rules-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Opty ID</th>
                  <th>Opportunity</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Score</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {analysisHistory.map((h) => (
                  <tr
                    key={h.id}
                    onClick={() => selectAnalysis(h.id, h)}
                    style={{
                      cursor: "pointer",
                      background: selectedAnalysisId === h.id ? "#eff6ff" : "transparent",
                    }}
                  >
                    <td>{h.id}</td>
                    <td title={h.sf_opportunity_id || ""} style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {h.sf_opportunity_id || "-"}
                    </td>
                    <td title={h.opportunity_name || ""} style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {h.opportunity_name}
                    </td>
                    <td title={h.account_name || ""} style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {h.account_name}
                    </td>
                    <td>{h.contract_type}</td>
                    <td>
                      <span style={{
                        fontWeight: 600,
                        color: getScoreColor(h.score_overall)
                      }}>
                        {h.score_overall}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: h.source === "SFDC" ? "#dbeafe" : "#f3e8ff",
                        color: h.source === "SFDC" ? "#1e40af" : "#7c3aed",
                      }}>
                        {h.source}
                      </span>
                    </td>
                  </tr>
                ))}
                {analysisHistory.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      {loading ? "Loading..." : "No analysis records found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* RIGHT PANEL */}
        {renderDetailCard()}
      </div>
    </div>
  );
}